/** The app's scroll container, and how bars pinned to its bottom edge keep focus clear of
 *  themselves.
 *
 *  The document never scrolls: pages scroll inside this box (AppShell, under the app bar),
 *  which is also the containing block of their absolutely positioned elements.
 *  A bar pinned to its bottom — the sticky `EditorSaveBar`, the records' fixed ledger sheet
 *  — covers the last strip of it, and the browser's focus scrolling (Tab, `focus()`,
 *  `scrollIntoView`) knows nothing of that: it brings a field just inside the scroller's
 *  edge, which is under the bar. So the scroller's `scroll-padding-bottom` is the height
 *  such a bar covers, which the bar measures and writes to a custom property here
 *  (`useBottomBarClearance`). Every scroll-into-view then stops that much short of the
 *  edge, for every focusable element: inputs and buttons scroll clear whole, and a
 *  textarea — for which Chrome reveals the caret, not the box — puts its caret line above
 *  the bar, while typing too. With no bar the property is unset and the padding is 0.
 *
 *  A FIXED bar (the records' ledger sheet below 1100 px) also covers the END of the page:
 *  whatever comes last — a page's footer links — would end under it with nowhere left to
 *  scroll. So the scroller ends in a spacer as tall as what its fixed bars cover
 *  (`FIXED_BAR_CLEARANCE_VAR`). A sticky bar (`EditorSaveBar`) takes room in the flow of
 *  its own and needs none.
 */
import { Box } from '@mui/material';
import { useLayoutEffect, type ReactNode, type RefObject } from 'react';

/** Marks the app scroller, so a bar finds the box to keep clear. */
export const APP_SCROLLER_ATTR = 'data-app-scroller';
/** Set on the app scroller: the px of its bottom edge a pinned bar covers, plus a gap. */
export const BAR_CLEARANCE_VAR = '--app-bar-clearance';
/** Set on the app scroller: the same for its FIXED bars only — the height of the spacer
 *  the scroll content ends with. */
export const FIXED_BAR_CLEARANCE_VAR = '--app-fixed-bar-clearance';
/** Between a focused element and the top of the bar. */
const GAP_PX = 8;

export function AppScroller({ children }: { children: ReactNode }) {
  return (
    <Box
      data-app-scroller=""
      sx={{
        flex: 1,
        minHeight: 0,
        overflow: 'auto',
        // The containing block of every absolutely positioned element of a page that has no
        // positioned ancestor of its own (a visually hidden label, a live region): it scrolls
        // and clips with the page here. Without it such an element is placed against the
        // document, outside every overflow box, and makes the document itself scroll.
        position: 'relative',
        p: 3,
        // A flex column so grid pages can stretch a ResourceDataGrid (layout="fill") to
        // the viewport — see layout/pageFill.ts.
        display: 'flex',
        flexDirection: 'column',
        scrollPaddingBottom: `var(${BAR_CLEARANCE_VAR}, 0px)`,
        // The end of the page scrolls clear of a fixed bar (0 without one).
        '&::after': { content: '""', display: 'block', flex: 'none', height: `var(${FIXED_BAR_CLEARANCE_VAR}, 0px)` },
      }}
    >
      {children}
    </Box>
  );
}

/** For a STICKY bar's own controls: a focused Save sits inside the very strip the padding
 *  excludes, and scrolling cannot move a stuck bar — the browser would scroll the page by
 *  the bar's height for nothing on every Tab onto it. A negative bottom margin of the same
 *  size keeps them "in view". (A fixed bar needs none: its focus never scrolls the page.) */
export const BAR_CONTROLS_SX = {
  '& *': { scrollMarginBottom: `calc(-1 * var(${BAR_CLEARANCE_VAR}, 0px))` },
} as const;

interface Covered {
  px: number;
  /** A fixed bar (not in the flow): the scroll content ends in a spacer for it. */
  fixed: boolean;
}

/** Each scroller's bars → what each covers. The scroller pads by the largest. */
const covered = new WeakMap<HTMLElement, Map<HTMLElement, Covered>>();

function setVar(scroller: HTMLElement, name: string, px: number) {
  if (px > 0) scroller.style.setProperty(name, `${px}px`);
  else scroller.style.removeProperty(name);
}

function publish(scroller: HTMLElement, bars: Map<HTMLElement, Covered>) {
  const all = [...bars.values()];
  setVar(scroller, BAR_CLEARANCE_VAR, Math.max(0, ...all.map((c) => c.px)));
  setVar(scroller, FIXED_BAR_CLEARANCE_VAR, Math.max(0, ...all.filter((c) => c.fixed).map((c) => c.px)));
}

/** How much of the scroller's bottom edge `bar` covers, plus the gap; 0 while hidden. */
function coveredBy(bar: HTMLElement, scroller: HTMLElement): Covered {
  const rect = bar.getBoundingClientRect();
  const style = getComputedStyle(bar);
  const fixed = style.position === 'fixed';
  if (rect.height === 0) return { px: 0, fixed };
  // A sticky bar rests on the scroller's bottom padding — content scrolls through that
  // strip under it — so it covers the padding too. A fixed one sits wherever it is.
  const below =
    style.position === 'sticky'
      ? (parseFloat(getComputedStyle(scroller).paddingBottom) || 0) + (parseFloat(style.bottom) || 0)
      : Math.max(0, scroller.getBoundingClientRect().bottom - rect.bottom);
  return { px: Math.ceil(rect.height + below + GAP_PX), fixed };
}

/** Keeps the app scroller's focus scrolling clear of `ref`, a bar pinned to its bottom edge,
 *  while `active`: measures the bar (a long block reason wraps it taller) and publishes what
 *  it covers as the scroller's `BAR_CLEARANCE_VAR` — and, for a fixed bar, as the spacer the
 *  scroll content ends with (`FIXED_BAR_CLEARANCE_VAR`). Outside an `AppScroller`, it does
 *  nothing. */
export function useBottomBarClearance(ref: RefObject<HTMLElement>, active = true): void {
  useLayoutEffect(() => {
    const bar = ref.current;
    const scroller = bar?.closest<HTMLElement>(`[${APP_SCROLLER_ATTR}]`);
    if (!active || !bar || !scroller) return undefined;
    const bars = covered.get(scroller) ?? new Map<HTMLElement, Covered>();
    covered.set(scroller, bars);
    const measure = () => {
      const now = coveredBy(bar, scroller);
      const was = bars.get(bar);
      if (was && was.px === now.px && was.fixed === now.fixed) return;
      bars.set(bar, now);
      publish(scroller, bars);
    };
    measure();
    const ro = new ResizeObserver(measure);
    ro.observe(bar);
    return () => {
      ro.disconnect();
      bars.delete(bar);
      publish(scroller, bars);
    };
  }, [ref, active]);
}
